import { m, AnimatePresence } from "framer-motion"
import { logger } from "@/utils/logger"
import { Truck, ShoppingBag, MapPin, X, ChevronRight, Clock } from "lucide-react"
import { useDeliveryStore } from "@/stores"
import { useNavigate } from "react-router-dom"
import { useState } from "react"
import { DeliveryAddressChecker } from "./DeliveryAddressChecker"
import { Button } from "../common/Button"
import { cn } from "@/utils/helpers"
import { useProducts } from '@/api_communication/hooks/common/useProducts'
import { useSalesStatus } from '@/api_communication/hooks/common/useSalesStatus'
import { useTimeEstimate } from '@/api_communication/hooks/manage/admin/useTimeThresholds'
import { useTranslation } from "react-i18next"
import { useCartStore } from "@/stores"
import { AnimatedModal } from "@/components/common/AnimatedModal"
import { trackRemoveFromCart } from "@/analytics/ga"

interface DeliverySelectionModalProps {
    isOpen: boolean
    onClose: () => void
    onMethodSelect?: (method: 'delivery' | 'pickup' | 'sushimat') => void
}

interface OptionCardProps {
    icon: React.ReactNode
    title: string
    description: string
    badge?: string
    timeLabel?: string
    timeValue?: string
    ecoFilled?: 1 | 2 | 3
    isActive: boolean
    isDisabled: boolean
    disabledReason?: string
    isSelected: boolean
    highlight?: boolean
    onClick: () => void
}

function EcoDots({ filled }: { filled: 1 | 2 | 3 }) {
    return (
        <div className="flex items-center gap-1.5 mt-auto pt-3 z-20 relative">
            {[1, 2, 3].map((i) => (
                <div
                    key={i}
                    className={cn(
                        "h-1.5 flex-1 rounded-full transition-all duration-700 ease-out",
                        i <= filled ? "bg-gradient-to-r from-emerald-500 to-emerald-400 shadow-[0_0_8px_rgba(16,185,129,0.4)]" : "bg-black/5"
                    )}
                />
            ))}
            <span className="text-[10px] font-black uppercase tracking-widest text-emerald-600/80 ml-1">EKO</span>
        </div>
    )
}

function OptionCard({
    icon, title, description, badge, timeLabel, timeValue,
    ecoFilled, isActive, isDisabled, disabledReason, isSelected, highlight, onClick,
}: OptionCardProps) {
    return (
        <button
            onClick={onClick}
            disabled={isDisabled}
            className={cn(
                "group relative flex w-full flex-col text-left rounded-[2rem] p-6 transition-all duration-500 ease-out outline-none overflow-hidden transform-gpu",
                isDisabled
                    ? "opacity-50 cursor-not-allowed bg-black/[0.02]"
                    : isSelected
                        ? "bg-white/80 border border-white shadow-[0_8px_40px_-12px_rgba(239,68,68,0.25)] scale-[1.02] ring-1 ring-red-500/20"
                        : "bg-white/40 border border-white/60 hover:bg-white/90 hover:border-white hover:shadow-[0_8px_30px_-12px_rgba(0,0,0,0.1)] hover:scale-[1.01] active:scale-[0.99] backdrop-blur-md"
            )}
        >
            {/* Highlight Glow */}
            {!isDisabled && !isSelected && highlight && (
                <div className="absolute inset-0 z-0 bg-gradient-to-br from-red-500/10 to-transparent pointer-events-none animate-pulse rounded-[2rem]" />
            )}

            {/* Liquid Background effect for selected */}
            <AnimatePresence>
                {isSelected && (
                    <m.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.5 }}
                        className="absolute inset-0 z-0 bg-gradient-to-br from-red-500/5 via-transparent to-black/5 pointer-events-none" 
                    />
                )}
            </AnimatePresence>

            <div className="relative z-10 flex flex-col h-full w-full">
                {/* Icon + Badge row */}
                <div className="flex items-start gap-4 mb-4">
                    <div className={cn(
                        "flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl transition-all duration-500 ease-out shadow-sm",
                        isSelected
                            ? "bg-gradient-to-br from-red-600 to-red-500 text-white shadow-[0_8px_20px_-6px_rgba(239,68,68,0.5)] scale-110"
                            : "bg-white/80 text-black/60 group-hover:bg-white group-hover:text-red-500 group-hover:scale-105 border border-white/50"
                    )}>
                        {icon}
                    </div>
                    <div className="flex-1 min-w-0 pt-1">
                        <div className="flex items-center gap-2 flex-wrap mb-0.5">
                            <h3 className={cn(
                                "font-black text-[17px] leading-tight tracking-tight transition-colors duration-300",
                                isSelected ? "text-black" : "text-black/80"
                            )}>
                                {title}
                            </h3>
                            {badge && (
                                <span className={cn(
                                    "px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-widest transition-colors duration-300",
                                    badge === 'EKO' 
                                        ? "bg-emerald-500/10 text-emerald-600" 
                                        : (isSelected ? "bg-red-500/10 text-red-600" : "bg-black/5 text-black/50")
                                )}>
                                    {badge}
                                </span>
                            )}
                        </div>
                         {/* Time / Status attached to header on desktop */}
                         <div className="mt-1">
                            {isActive ? (
                                timeValue && (
                                    <div className={cn(
                                        "inline-flex items-center gap-1.5 text-[12px] font-bold transition-colors duration-300",
                                        isSelected ? "text-red-500" : "text-black/40"
                                    )}>
                                        <Clock className="w-3.5 h-3.5" />
                                        <span>{timeLabel} {timeValue}</span>
                                    </div>
                                )
                            ) : (
                                <div className="text-[11px] font-black text-red-500/80 uppercase tracking-widest">
                                    {disabledReason || 'Niedostępne'}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Description */}
                <p className={cn(
                    "text-[13px] leading-relaxed font-semibold line-clamp-3 transition-colors duration-300",
                    isSelected ? "text-black/60" : "text-black/40"
                )}>
                    {description}
                </p>

                {/* Eco bar */}
                {isActive && ecoFilled && (
                    <EcoDots filled={ecoFilled} />
                )}
            </div>
            
            {/* Selected indicator replaced by liquid styling above, keeping a subtle glow line */}
            <AnimatePresence>
                {isSelected && (
                    <m.div 
                        initial={{ opacity: 0, scaleX: 0 }}
                        animate={{ opacity: 1, scaleX: 1 }}
                        exit={{ opacity: 0, scaleX: 0 }}
                        className="absolute bottom-0 left-10 right-10 h-1 bg-gradient-to-r from-transparent via-red-500 to-transparent rounded-t-full opacity-60"
                        style={{ originX: 0.5 }}
                    />
                )}
            </AnimatePresence>
        </button>
    )
}

export const DeliverySelectionModal = ({ isOpen, onClose, onMethodSelect }: DeliverySelectionModalProps) => {
    const { t } = useTranslation()
    const { deliveryMethod, setDeliveryMethod } = useDeliveryStore()
    const { data: estDelivery } = useTimeEstimate('delivery', isOpen)
    const { data: estPickup } = useTimeEstimate('pickup', isOpen)
    const { data: salesStatus } = useSalesStatus()
    const { data: allProducts = [] } = useProducts()
    const { items: cartItems, removeItem } = useCartStore()
    const navigate = useNavigate()
    const [step, setStep] = useState<'selection' | 'address'>('selection')
    const [conflictModalOpen, setConflictModalOpen] = useState(false)
    const [pendingMethod, setPendingMethod] = useState<'delivery' | 'pickup' | 'sushimat' | null>(null)
    const [conflictingItems, setConflictingItems] = useState<typeof cartItems>([])

    const getStatus = (method: string) => {
        const statuses = Array.isArray(salesStatus) ? salesStatus : []
        return statuses.find((s: any) => s.method === method) || { is_active: true, disabled_reason: '' }
    }
    const stDelivery = getStatus('delivery')
    const stPickup = getStatus('pickup')
    const stSushimat = getStatus('sushimat')

    const handleSelect = (method: 'delivery' | 'pickup' | 'sushimat') => {
        logger.addBreadcrumb({ category: 'delivery', message: 'User attempting to select delivery method', data: { method } })
        if (cartItems.length > 0) {
            const conflicts = cartItems.filter(cartItem => {
                const product = allProducts.find(p => p.id === cartItem.productId)
                if (!product) return false
                if (cartItem.isCustomSet) return false
                if (method === 'delivery') return product.is_delivery === false
                if (method === 'pickup') return product.is_pickup === false
                if (method === 'sushimat') return product.is_sushimat === false
                return false
            })
            if (conflicts.length > 0) {
                setConflictingItems(conflicts)
                setPendingMethod(method)
                setConflictModalOpen(true)
                return
            }
        }
        proceedWithMethod(method)
    }

    const proceedWithMethod = (method: 'delivery' | 'pickup' | 'sushimat') => {
        if (method === 'delivery') {
            setStep('address')
            return
        }
        if (onMethodSelect) {
            onMethodSelect(method)
        } else {
            setDeliveryMethod(method)
            onClose()
            navigate('/menu')
        }
    }

    const handleConfirmConflict = () => {
        logger.addBreadcrumb({ category: 'delivery', message: 'User confirmed delivery conflict (removing items)', data: { itemsRemoved: conflictingItems.map(i => i.name) } })
        trackRemoveFromCart(conflictingItems)
        conflictingItems.forEach(item => removeItem(item.id))
        setConflictModalOpen(false)
        if (pendingMethod) proceedWithMethod(pendingMethod)
    }

    const handleAddressVerified = () => {
        setTimeout(() => {
            if (onMethodSelect) {
                onMethodSelect('delivery')
            } else {
                setDeliveryMethod('delivery')
                onClose()
                navigate('/menu')
            }
        }, 1500)
    }

    const handleSkipAddress = () => {
        logger.addBreadcrumb({ category: 'delivery', message: 'User skipped address verification' })
        if (cartItems.length > 0) {
            const conflicts = cartItems.filter(cartItem => {
                const product = allProducts.find(p => p.id === cartItem.productId)
                if (!product) return false
                if (cartItem.isCustomSet) return false
                return product.is_delivery === false
            })
            if (conflicts.length > 0) {
                setConflictingItems(conflicts)
                setPendingMethod('delivery')
                setConflictModalOpen(true)
                return
            }
        }
        setDeliveryMethod('delivery')
        onClose()
        navigate('/menu')
    }

    return (
        <>
        <AnimatedModal isOpen={isOpen} onClose={onClose} className="!max-w-4xl !bg-white/60 !backdrop-blur-2xl !rounded-[2.5rem] sm:!rounded-[3rem] border border-white/50 shadow-[0_40px_100px_-20px_rgba(0,0,0,0.3),inset_0_1px_0_rgba(255,255,255,0.8)] overflow-hidden">
            <div className="flex flex-col relative max-h-[92dvh]">
                            {/* Subtle premium gradient wash */}
                            <div className="absolute inset-0 bg-gradient-to-br from-white/80 via-white/40 to-black/5 pointer-events-none z-0" />

                            {/* Header */}
                            <div className="px-8 pt-8 pb-6 flex items-start justify-between flex-shrink-0 relative z-10 border-b border-black/[0.03]">
                                <div>
                                    <p className="text-[11px] font-black uppercase tracking-[0.2em] text-red-600/80 mb-2 drop-shadow-sm">
                                        {step === 'selection' ? 'SushiKing ' : 'Dostawa'}
                                    </p>
                                    <h2 className="text-3xl sm:text-4xl font-black tracking-[-0.03em] text-black leading-none drop-shadow-sm">
                                        {step === 'selection' ? 'Wybierz formę odbioru' : 'Gdzie dostarczyć?'}
                                    </h2>
                                    <p className="text-[15px] sm:text-[16px] text-black/50 font-semibold mt-3">
                                        {step === 'selection'
                                            ? 'Dostarczymy Twoje sushi dokładnie tak, jak tego chcesz.'
                                            : 'Podaj adres i sprawdźmy, czy dojeżdżamy w Twój rejon.'}
                                    </p>
                                </div>
                                <button
                                    onClick={onClose}
                                    className="group flex-shrink-0 ml-6 w-12 h-12 flex items-center justify-center rounded-[1.25rem] bg-white/60 hover:bg-white shadow-sm border border-white/60 hover:border-white transition-all duration-300 active:scale-95"
                                >
                                    <X className="w-5 h-5 text-black/40 group-hover:text-black transition-colors" />
                                </button>
                            </div>

                            {/* Content */}
                            <div className="px-5 sm:px-8 py-6 sm:py-8 overflow-y-auto flex-1 relative z-10 custom-scrollbar">
                                <AnimatePresence mode="wait">
                                    {step === 'selection' ? (
                                        <m.div
                                            key="selection"
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: -20 }}
                                            transition={{ duration: 0.3, ease: "easeOut" }}
                                        >
                                            {/* Cards layout */}
                                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 lg:gap-6">
                                                <OptionCard
                                                    icon={<Truck className="w-7 h-7" />}
                                                    title={t('dostawaNaAdres')}
                                                    description={`${t('kosztDostawyZaleznyOdOdleglosciLinia1')} ${t('kosztDostawyZaleznyOdOdleglosciLinia2')}`}
                                                    timeLabel="ok."
                                                    timeValue={stDelivery.is_active ? `${estDelivery?.estimated_time_minutes ?? '...'} min` : undefined}
                                                    ecoFilled={1}
                                                    isActive={stDelivery.is_active}
                                                    isDisabled={!stDelivery.is_active}
                                                    disabledReason={stDelivery.disabled_reason}
                                                    isSelected={deliveryMethod === 'delivery'}
                                                    onClick={() => handleSelect('delivery')}
                                                />
                                                <OptionCard
                                                    icon={<ShoppingBag className="w-7 h-7" />}
                                                    title={t('odbiorOsobisty')}
                                                    description={t('adresOdbioruOsobistego')}
                                                    timeLabel="ok."
                                                    timeValue={stPickup.is_active ? `${estPickup?.estimated_time_minutes ?? '...'} min` : undefined}
                                                    ecoFilled={2}
                                                    isActive={stPickup.is_active}
                                                    isDisabled={!stPickup.is_active}
                                                    disabledReason={stPickup.disabled_reason}
                                                    isSelected={deliveryMethod === 'pickup'}
                                                    highlight={true}
                                                    onClick={() => handleSelect('pickup')}
                                                />
                                                <OptionCard
                                                    icon={<MapPin className="w-7 h-7" />}
                                                    title={t('sushimat')}
                                                    description={t('wybierzPunktZMapy')}
                                                    badge="EKO"
                                                    timeLabel=""
                                                    timeValue={stSushimat.is_active ? t('dostawaJutro1200') : undefined}
                                                    ecoFilled={3}
                                                    isActive={stSushimat.is_active}
                                                    isDisabled={!stSushimat.is_active}
                                                    disabledReason={stSushimat.disabled_reason}
                                                    isSelected={deliveryMethod === 'sushimat'}
                                                    onClick={() => handleSelect('sushimat')}
                                                />
                                            </div>

                                            <p className="mt-8 mb-2 text-center text-[10px] font-black uppercase tracking-[0.25em] text-black/20 drop-shadow-sm">
                                                {t('modalEkoDisclaimer')}
                                            </p>
                                        </m.div>
                                    ) : (
                                        <m.div
                                            key="address"
                                            initial={{ opacity: 0, x: 20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: 20 }}
                                            transition={{ duration: 0.3, ease: "easeOut" }}
                                            className="space-y-6 max-w-2xl mx-auto"
                                        >
                                            <DeliveryAddressChecker onVerified={handleAddressVerified} />

                                            <div className="flex flex-col sm:flex-row justify-between items-center py-2 relative z-10 gap-3 pb-[80px] sm:pb-0">
                                                <button
                                                    onClick={() => setStep('selection')}
                                                    className="flex items-center justify-center w-full sm:w-auto gap-2 text-[14px] font-black text-black/40 hover:text-black transition-colors order-2 sm:order-1 pt-2 sm:pt-0"
                                                >
                                                    <ChevronRight className="w-4 h-4 rotate-180" />
                                                    {t('wrocDoWyboru')}
                                                </button>
                                                <Button variant="ghost" onClick={handleSkipAddress} className="rounded-2xl hover:bg-black/5 font-black w-full sm:w-auto order-1 sm:order-2 border border-black/5">
                                                    {t('pomineToTeraz')}
                                                </Button>
                                            </div>
                                        </m.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </div>
        </AnimatedModal>

        {/* Conflict Resolution Modal stays centered */}
        <AnimatedModal isOpen={conflictModalOpen} onClose={() => setConflictModalOpen(false)} className="!max-w-[22rem] !p-8 !bg-white/80 !backdrop-blur-2xl !rounded-[2.5rem] border border-white shadow-[0_40px_80px_-20px_rgba(0,0,0,0.4)]">
            <div className="text-center relative z-10">
                        <div className="w-16 h-16 rounded-[1.5rem] bg-gradient-to-br from-red-500 to-red-600 shadow-[0_8px_20px_rgba(239,68,68,0.4)] flex items-center justify-center mx-auto mb-6">
                            <ShoppingBag className="w-8 h-8 text-white" />
                        </div>
                        <h3 className="text-[20px] font-black tracking-[-0.02em] text-black mb-3">Zmiana metody</h3>
                        <p className="text-black/60 mb-6 text-[14px] font-semibold leading-relaxed">
                            Część produktów w koszyku nie obsługuje tej formy dostawy i zostanie automatycznie usunięta:
                        </p>
                        <ul className="text-[13px] text-left mb-8 bg-black/5 rounded-2xl p-5 max-h-40 overflow-y-auto space-y-3 shadow-inner">
                            {conflictingItems.map((item, idx) => (
                                <li key={idx} className="font-bold text-black flex items-center gap-3">
                                    <div className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0 shadow-[0_0_8px_rgba(239,68,68,0.5)]" />
                                    {item.name}
                                </li>
                            ))}
                        </ul>
                        <div className="flex gap-4">
                            <Button variant="ghost" onClick={() => setConflictModalOpen(false)} className="flex-1 rounded-[1.25rem] font-bold bg-white/50 hover:bg-white border border-white/50">
                                Anuluj
                            </Button>
                            <Button variant="primary" onClick={handleConfirmConflict} className="flex-1 rounded-[1.25rem] font-bold shadow-[0_8px_20px_rgba(0,0,0,0.15)]">
                                Usuń i kontynuuj
                            </Button>
                        </div>
            </div>
        </AnimatedModal>
        </>
    )
}
